/**
 * The web classes of each colour tone (@tonnie/design-tokens `TONES`).
 *
 * Whole class names, so Tailwind generates every one of them: code that
 * chooses a tone at runtime (a MESH letter, a status) indexes this map and
 * never builds `bg-tone-${tone}-solid` from parts — a class assembled at
 * runtime is invisible to Tailwind and ships unstyled.
 */
import { HERO_TONE, type HeroType, type Tone } from '@tonnie/design-tokens';

export interface ToneClasses {
  /** Text or icon on the page, a card or the tone's tint. */
  text: string;
  /** Hover / emphasis text. */
  strong: string;
  /** The tone's tint, as a background. */
  subtle: string;
  /** A border on the tint. */
  line: string;
  /** The line on hover — a choice that can be taken. */
  lineHover: string;
  /** A border in the fill colour — a choice taken. */
  edge: string;
  /** A ring on the tint's line — a mark that is current (an open trip). */
  ringLine: string;
  /** A fill; pair with `onSolid` for text on it. */
  solid: string;
  onSolid: string;
  /** The fill hovered — a shade away from its ink, never faded (tones.spec.ts). */
  solidHover: string;
  /** A chosen toggle (`data-state=on`): the fill, its ink and its hover. */
  selected: string;
  /** A switch turned on (`data-state=checked`): the fill. */
  checked: string;
  /** An SVG stroke in the fill colour (rings, arcs). */
  stroke: string;
  /** The top border in the fill colour — a block's accent rule. */
  topEdge: string;
  /** The fill's ink as the ground and the fill as the ink — a stamp on a fill. */
  inverse: string;
}

export const TONE_CLASSES: Record<Tone, ToneClasses> = {
  green: {
    text: 'text-tone-green',
    strong: 'text-tone-green-strong',
    subtle: 'bg-tone-green-subtle',
    line: 'border-tone-green-line',
    lineHover: 'hover:border-tone-green-line',
    edge: 'border-tone-green-solid',
    ringLine: 'ring-tone-green-line',
    solid: 'bg-tone-green-solid',
    onSolid: 'text-tone-green-on-solid',
    solidHover: 'hover:bg-tone-green-solid-hover',
    selected:
      'data-[state=on]:bg-tone-green-solid data-[state=on]:text-tone-green-on-solid data-[state=on]:hover:bg-tone-green-solid-hover',
    checked: 'data-[state=checked]:bg-tone-green-solid',
    stroke: 'stroke-tone-green-solid',
    topEdge: 'border-t-tone-green-solid',
    inverse: 'bg-tone-green-on-solid text-tone-green-solid',
  },
  amber: {
    text: 'text-tone-amber',
    strong: 'text-tone-amber-strong',
    subtle: 'bg-tone-amber-subtle',
    line: 'border-tone-amber-line',
    lineHover: 'hover:border-tone-amber-line',
    edge: 'border-tone-amber-solid',
    ringLine: 'ring-tone-amber-line',
    solid: 'bg-tone-amber-solid',
    onSolid: 'text-tone-amber-on-solid',
    solidHover: 'hover:bg-tone-amber-solid-hover',
    selected:
      'data-[state=on]:bg-tone-amber-solid data-[state=on]:text-tone-amber-on-solid data-[state=on]:hover:bg-tone-amber-solid-hover',
    checked: 'data-[state=checked]:bg-tone-amber-solid',
    stroke: 'stroke-tone-amber-solid',
    topEdge: 'border-t-tone-amber-solid',
    inverse: 'bg-tone-amber-on-solid text-tone-amber-solid',
  },
  orange: {
    text: 'text-tone-orange',
    strong: 'text-tone-orange-strong',
    subtle: 'bg-tone-orange-subtle',
    line: 'border-tone-orange-line',
    lineHover: 'hover:border-tone-orange-line',
    edge: 'border-tone-orange-solid',
    ringLine: 'ring-tone-orange-line',
    solid: 'bg-tone-orange-solid',
    onSolid: 'text-tone-orange-on-solid',
    solidHover: 'hover:bg-tone-orange-solid-hover',
    selected:
      'data-[state=on]:bg-tone-orange-solid data-[state=on]:text-tone-orange-on-solid data-[state=on]:hover:bg-tone-orange-solid-hover',
    checked: 'data-[state=checked]:bg-tone-orange-solid',
    stroke: 'stroke-tone-orange-solid',
    topEdge: 'border-t-tone-orange-solid',
    inverse: 'bg-tone-orange-on-solid text-tone-orange-solid',
  },
  red: {
    text: 'text-tone-red',
    strong: 'text-tone-red-strong',
    subtle: 'bg-tone-red-subtle',
    line: 'border-tone-red-line',
    lineHover: 'hover:border-tone-red-line',
    edge: 'border-tone-red-solid',
    ringLine: 'ring-tone-red-line',
    solid: 'bg-tone-red-solid',
    onSolid: 'text-tone-red-on-solid',
    solidHover: 'hover:bg-tone-red-solid-hover',
    selected:
      'data-[state=on]:bg-tone-red-solid data-[state=on]:text-tone-red-on-solid data-[state=on]:hover:bg-tone-red-solid-hover',
    checked: 'data-[state=checked]:bg-tone-red-solid',
    stroke: 'stroke-tone-red-solid',
    topEdge: 'border-t-tone-red-solid',
    inverse: 'bg-tone-red-on-solid text-tone-red-solid',
  },
  rose: {
    text: 'text-tone-rose',
    strong: 'text-tone-rose-strong',
    subtle: 'bg-tone-rose-subtle',
    line: 'border-tone-rose-line',
    lineHover: 'hover:border-tone-rose-line',
    edge: 'border-tone-rose-solid',
    ringLine: 'ring-tone-rose-line',
    solid: 'bg-tone-rose-solid',
    onSolid: 'text-tone-rose-on-solid',
    solidHover: 'hover:bg-tone-rose-solid-hover',
    selected:
      'data-[state=on]:bg-tone-rose-solid data-[state=on]:text-tone-rose-on-solid data-[state=on]:hover:bg-tone-rose-solid-hover',
    checked: 'data-[state=checked]:bg-tone-rose-solid',
    stroke: 'stroke-tone-rose-solid',
    topEdge: 'border-t-tone-rose-solid',
    inverse: 'bg-tone-rose-on-solid text-tone-rose-solid',
  },
  blue: {
    text: 'text-tone-blue',
    strong: 'text-tone-blue-strong',
    subtle: 'bg-tone-blue-subtle',
    line: 'border-tone-blue-line',
    lineHover: 'hover:border-tone-blue-line',
    edge: 'border-tone-blue-solid',
    ringLine: 'ring-tone-blue-line',
    solid: 'bg-tone-blue-solid',
    onSolid: 'text-tone-blue-on-solid',
    solidHover: 'hover:bg-tone-blue-solid-hover',
    selected:
      'data-[state=on]:bg-tone-blue-solid data-[state=on]:text-tone-blue-on-solid data-[state=on]:hover:bg-tone-blue-solid-hover',
    checked: 'data-[state=checked]:bg-tone-blue-solid',
    stroke: 'stroke-tone-blue-solid',
    topEdge: 'border-t-tone-blue-solid',
    inverse: 'bg-tone-blue-on-solid text-tone-blue-solid',
  },
  cyan: {
    text: 'text-tone-cyan',
    strong: 'text-tone-cyan-strong',
    subtle: 'bg-tone-cyan-subtle',
    line: 'border-tone-cyan-line',
    lineHover: 'hover:border-tone-cyan-line',
    edge: 'border-tone-cyan-solid',
    ringLine: 'ring-tone-cyan-line',
    solid: 'bg-tone-cyan-solid',
    onSolid: 'text-tone-cyan-on-solid',
    solidHover: 'hover:bg-tone-cyan-solid-hover',
    selected:
      'data-[state=on]:bg-tone-cyan-solid data-[state=on]:text-tone-cyan-on-solid data-[state=on]:hover:bg-tone-cyan-solid-hover',
    checked: 'data-[state=checked]:bg-tone-cyan-solid',
    stroke: 'stroke-tone-cyan-solid',
    topEdge: 'border-t-tone-cyan-solid',
    inverse: 'bg-tone-cyan-on-solid text-tone-cyan-solid',
  },
  violet: {
    text: 'text-tone-violet',
    strong: 'text-tone-violet-strong',
    subtle: 'bg-tone-violet-subtle',
    line: 'border-tone-violet-line',
    lineHover: 'hover:border-tone-violet-line',
    edge: 'border-tone-violet-solid',
    ringLine: 'ring-tone-violet-line',
    solid: 'bg-tone-violet-solid',
    onSolid: 'text-tone-violet-on-solid',
    solidHover: 'hover:bg-tone-violet-solid-hover',
    selected:
      'data-[state=on]:bg-tone-violet-solid data-[state=on]:text-tone-violet-on-solid data-[state=on]:hover:bg-tone-violet-solid-hover',
    checked: 'data-[state=checked]:bg-tone-violet-solid',
    stroke: 'stroke-tone-violet-solid',
    topEdge: 'border-t-tone-violet-solid',
    inverse: 'bg-tone-violet-on-solid text-tone-violet-solid',
  },
  slate: {
    text: 'text-tone-slate',
    strong: 'text-tone-slate-strong',
    subtle: 'bg-tone-slate-subtle',
    line: 'border-tone-slate-line',
    lineHover: 'hover:border-tone-slate-line',
    edge: 'border-tone-slate-solid',
    ringLine: 'ring-tone-slate-line',
    solid: 'bg-tone-slate-solid',
    onSolid: 'text-tone-slate-on-solid',
    solidHover: 'hover:bg-tone-slate-solid-hover',
    selected:
      'data-[state=on]:bg-tone-slate-solid data-[state=on]:text-tone-slate-on-solid data-[state=on]:hover:bg-tone-slate-solid-hover',
    checked: 'data-[state=checked]:bg-tone-slate-solid',
    stroke: 'stroke-tone-slate-solid',
    topEdge: 'border-t-tone-slate-solid',
    inverse: 'bg-tone-slate-on-solid text-tone-slate-solid',
  },
};

/**
 * The web classes of each hero: its tone's (`HERO_TONE`), and for Passport the
 * brand's — the same colours `HERO_COLORS` gives as CSS values, for code that
 * only needs a class.
 */
export const HERO_CLASSES: Record<HeroType, ToneClasses> = {
  checkup: TONE_CLASSES[HERO_TONE.checkup],
  teleportal: TONE_CLASSES[HERO_TONE.teleportal],
  qultureup: TONE_CLASSES[HERO_TONE.qultureup],
  care: TONE_CLASSES[HERO_TONE.care],
  testit: TONE_CLASSES[HERO_TONE.testit],
  inout: TONE_CLASSES[HERO_TONE.inout],
  passport: {
    text: 'text-primary-accessible',
    strong: 'text-primary-accessible',
    subtle: 'bg-primary/10',
    line: 'border-primary/20',
    lineHover: 'hover:border-primary/20',
    edge: 'border-primary',
    ringLine: 'ring-primary/20',
    solid: 'bg-primary',
    onSolid: 'text-primary-foreground',
    solidHover: 'hover:bg-primary/90',
    selected:
      'data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:hover:bg-primary/90',
    checked: 'data-[state=checked]:bg-primary',
    stroke: 'stroke-primary',
    topEdge: 'border-t-primary',
    inverse: 'bg-primary-foreground text-primary-accessible-on-light',
  },
};
